import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** 0 — не закрывать автоматически (ошибки, требующие реакции). */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends Required<Pick<ToastOptions, 'title' | 'tone' | 'duration'>> {
  id: string;
  description?: string;
  action?: ToastOptions['action'];
}

interface ToastContextValue {
  toast: (options: ToastOptions) => string;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
} as const;

const TONES: Record<ToastTone, string> = {
  info: 'border-info-border [&_svg]:text-info',
  success: 'border-success-border [&_svg]:text-success',
  warning: 'border-warning-border [&_svg]:text-warning',
  danger: 'border-danger-border [&_svg]:text-danger',
};

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const item: ToastItem = {
        id,
        title: options.title,
        tone: options.tone ?? 'info',
        duration: options.duration ?? (options.tone === 'danger' ? 8000 : 4000),
        ...(options.description ? { description: options.description } : {}),
        ...(options.action ? { action: options.action } : {}),
      };
      setItems((prev) => [...prev.slice(-4), item]);
      if (item.duration > 0) setTimeout(() => dismiss(id), item.duration);
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ title, tone: 'success', ...(description ? { description } : {}) }),
      error: (title, description) => toast({ title, tone: 'danger', ...(description ? { description } : {}) }),
    }),
    [toast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed right-4 bottom-4 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2"
          style={{ zIndex: 'var(--z-toast)' }}
          role="region"
          aria-live="polite"
          aria-label="Уведомления"
        >
          {items.map((item) => {
            const Icon = ICONS[item.tone];
            return (
              <div
                key={item.id}
                role={item.tone === 'danger' ? 'alert' : 'status'}
                className={cn(
                  'pointer-events-auto flex items-start gap-2.5 rounded-lg border bg-surface-raised px-3 py-2.5 shadow-lg',
                  TONES[item.tone],
                )}
              >
                <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg">{item.title}</p>
                  {item.description ? (
                    <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">{item.description}</p>
                  ) : null}
                  {item.action ? (
                    <button
                      type="button"
                      onClick={() => {
                        item.action?.onClick();
                        dismiss(item.id);
                      }}
                      className="mt-1.5 text-xs font-medium text-accent-text underline-offset-2 hover:underline"
                    >
                      {item.action.label}
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(item.id)}
                  aria-label="Закрыть уведомление"
                  className="rounded-xs p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-fg"
                >
                  <X className="size-3.5" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast должен вызываться внутри ToastProvider');
  return context;
}
